import { Button, Col, Row, Select, Table, Card, message, DatePicker } from 'antd';
import { useEffect, useState } from 'react';
import { MultiaxisLineChart } from 'charts/MultiaxisLineChart';
import {
      getFilterOptions,
      getDataStatistics,
      getGraphicalStatistics,
      saveSnapshot,
      refreshSnapshot
} from 'services/CampaignReport';

import {
      PAGE_TITLE,
      EXPORT_FILENAME
} from './constants';
import { formatNumber } from 'helpers';
// import checkPermissionFunction from 'Permission'; // Uncomment if permission is strictly required
import { useDispatch } from 'react-redux';
import { setAppLoading } from 'app/slices/appSlice';
import dayjs from 'dayjs';

function CampaignReport() {
      const dispatch = useDispatch();

      // =========================
      // STATE
      // =========================

      // Data sources
      const [campaigns, setCampaigns] = useState([]);
      const [snapshotTimes, setSnapshotTimes] = useState([]);

      // Selections
      const [selectedCampaignId, setSelectedCampaignId] = useState(null);
      const [selectedSnapshotTime, setSelectedSnapshotTime] = useState(null);
      const [currentMetrics, setCurrentMetrics] = useState([]); // Metrics of current campaign

      // Chart Config
      // Chart loads 1 metric at a time. Range is implied or fixed (image doesn't show range picker, but integration guide mentions using startTime/endTime. 
      // Requirement says "Mặc định chọn mốc mới nhất". Chart usually shows a trend. Guide says "Vẽ biểu đồ lịch sử".
      // I will use a default range (e.g., 30 days) or just rely on API defaults if any.
      // For now I'll create a local state for Chart Filter (Metric + TimeRange if needed).
      const [chartMetricId, setChartMetricId] = useState(null);
      const [chartData, setChartData] = useState(null);

      const [chartDateRange, setChartDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);

      // Refresh/Save States
      const [isRefreshing, setIsRefreshing] = useState(false);
      const [isSaving, setIsSaving] = useState(false);
      const [isPreviewing, setIsPreviewing] = useState(false);

      // Table Data
      const [tableData, setTableData] = useState([]);
      const [tableHeaderTime, setTableHeaderTime] = useState('');

      // Helper Maps
      const [metricMap, setMetricMap] = useState({}); // id -> name/unit

      // =========================
      // EFFECTS
      // =========================

      // 1. Initial Load
      useEffect(() => {
            async function init() {
                  dispatch(setAppLoading(true));
                  try {
                        const res = await getFilterOptions();
                        if (res?.data?.data?.campaigns) {
                              // Process campaigns: Reverse metrics for each
                              const camps = (res.data.data.campaigns || []).map(c => ({
                                    ...c,
                                    metrics: (c.metrics || []).slice().reverse()
                              }));
                              setCampaigns(camps);

                              if (camps.length > 0) {
                                    // Select first campaign by default
                                    handleSelectCampaign(camps[0]);
                              }
                        }
                  } catch (error) {
                        console.error("Error loading filter options", error);
                        message.error("Không thể tải danh sách chiến dịch");
                  } finally {
                        dispatch(setAppLoading(false));
                  }
            }
            init();
      }, []);

      // =========================
      // HANDLERS
      // =========================

      const handleSelectCampaign = async (campaign) => {
            setChartData(null); // Clear previous chart
            setSelectedCampaignId(campaign.id);

            // Reverse metrics order
            const metrics = (campaign.metrics || []).slice().reverse();
            setCurrentMetrics(metrics);

            // Map metrics for easy lookup
            const mMap = {};
            metrics.forEach(m => mMap[m.id] = m);
            setMetricMap(mMap);

            // Default Chart Metric: First metric
            if (metrics.length > 0) {
                  setChartMetricId(metrics[0].id);
            } else {
                  setChartMetricId(null);
            }

            // Set Snapshots
            const snaps = campaign.snapshotTimes || [];
            setSnapshotTimes(snaps);

            // Select latest snapshot (first one)
            const latestInfo = snaps.length > 0 ? snaps[0] : null;
            setSelectedSnapshotTime(latestInfo);

            // Reset Date Range to 7 days
            const defaultRange = [dayjs().subtract(7, 'day'), dayjs()];
            setChartDateRange(defaultRange);

            setIsPreviewing(false); // Reset preview mode

            // Load Data
            await loadAllData(campaign.id, latestInfo, metrics?.[0]?.id, defaultRange);
      };

      const loadAllData = async (campId, snapTime, chartMetId, dateRange) => {
            dispatch(setAppLoading(true));
            try {
                  // 1. Load Table Data
                  // If snapTime is null, API might use latest.
                  const tableRes = await getDataStatistics({
                        campaignIds: [campId],
                        endTime: snapTime, // API expects specific time string
                        dimensionIds: [1] // Required by API
                        // metricIds: [] // get all
                  });

                  if (tableRes?.data?.data?.statistics) {
                        // Convert to simpler format for Antd Table if needed, or use as is
                        // API returns: [[{name:.., value:..}, {name:.., value:..}], ...]
                        // Requirement image shows simple key-value rows. 
                        // Let's see the API Example response in Guide:
                        // statistics: [[{name: "Ten", value: "A"}, {name: "Gia tri", value: 100}], ...]
                        // We need to flatten this.
                        const processedTable = (tableRes.data.data.statistics || []).map((row, index) => {
                              // row is an array of cells. We assume strict order or find by name.
                              // Guide says: Table chỉ có 2 cột: "Tên số liệu", "Giá trị"
                              const nameCell = row.find(r => r.name === "Tên số liệu") || row[0];
                              const valCell = row.find(r => r.name === "Giá trị") || row[1];
                              return {
                                    key: index,
                                    name: nameCell?.value,
                                    value: valCell?.value
                              };
                        });
                        setTableData(processedTable);
                        setTableHeaderTime(snapTime || dayjs().format('DD/MM/YYYY HH:mm')); // Fallback if no snapTime
                  }

                  // 2. Load Chart Data
                  // Use provided dateRange or state (though state might be stale in this closure if not passed)
                  const [start, end] = dateRange || chartDateRange;
                  if (chartMetId && start && end) {
                        const chartRes = await getGraphicalStatistics({
                              campaignIds: [campId],
                              metricIds: [chartMetId],
                              startTime: start.format('DD/MM/YYYY'),
                              endTime: end.format('DD/MM/YYYY'),
                              dimensionIds: []
                        });

                        // Format for MultiaxisLineChart
                        // Expected format: { name: "...", unit: "...", data: [{name: "...", data: [{date:..., value:...}]}] }
                        if (chartRes?.data?.data) {
                              setChartData(chartRes.data.data);
                        } else {
                              setChartData(null);
                        }
                  } else {
                        setChartData(null);
                  }

            } catch (error) {
                  console.error("Error loading data", error);
            } finally {
                  dispatch(setAppLoading(false));
            }
      };

      const handleSnapshotClick = async (time) => {
            setSelectedSnapshotTime(time);
            setIsPreviewing(false); // Exit preview mode
            dispatch(setAppLoading(true));
            try {
                  const tableRes = await getDataStatistics({
                        campaignIds: [selectedCampaignId],
                        endTime: time,
                        dimensionIds: [1] // Required by API
                  });

                  if (tableRes?.data?.data?.statistics) {
                        const processedTable = (tableRes.data.data.statistics || []).map((row, index) => {
                              const nameCell = row.find(r => r.name === "Tên số liệu") || row[0];
                              const valCell = row.find(r => r.name === "Giá trị") || row[1];
                              return {
                                    key: index,
                                    name: nameCell?.value,
                                    value: valCell?.value
                              };
                        });
                        setTableData(processedTable);
                        setTableHeaderTime(time);
                  }
            } catch (e) {
                  console.error(e);
            } finally {
                  dispatch(setAppLoading(false));
            }
      };

      const handleChartMetricChange = async (val) => {
            setChartMetricId(val);
            dispatch(setAppLoading(true));
            try {
                  const [start, end] = chartDateRange;
                  const chartRes = await getGraphicalStatistics({
                        campaignIds: [selectedCampaignId],
                        metricIds: [val],
                        startTime: start.format('DD/MM/YYYY'),
                        endTime: end.format('DD/MM/YYYY'),
                        dimensionIds: []
                  });
                  if (chartRes?.data?.data) {
                        setChartData(chartRes.data.data);
                  } else {
                        setChartData(null);
                  }
            } catch (e) {
                  console.error(e);
            } finally {
                  dispatch(setAppLoading(false));
            }
      };

      const handleDateRangeChange = async (dates) => {
            setChartDateRange(dates);
            if (!dates || dates.length < 2) return;

            dispatch(setAppLoading(true));
            try {
                  const [start, end] = dates;
                  const chartRes = await getGraphicalStatistics({
                        campaignIds: [selectedCampaignId],
                        metricIds: [chartMetricId],
                        startTime: start.format('DD/MM/YYYY'),
                        endTime: end.format('DD/MM/YYYY'),
                        dimensionIds: []
                  });
                  if (chartRes?.data?.data) {
                        setChartData(chartRes.data.data);
                  } else {
                        setChartData(null);
                  }
            } catch (e) {
                  console.error(e);
            } finally {
                  dispatch(setAppLoading(false));
            }
      };

      const handleRefreshSnapshot = async () => {
            setIsRefreshing(true);
            try {
                  const res = await refreshSnapshot({ campaignId: selectedCampaignId });
                  if (res?.data?.code === 200) {
                        const data = res.data.data;
                        if (data?.statistics) {
                              const processedTable = (data.statistics || []).map((row, index) => {
                                    const nameCell = row.find(r => r.name === "Tên số liệu") || row[0];
                                    const valCell = row.find(r => r.name === "Giá trị") || row[1];
                                    return {
                                          key: index,
                                          name: nameCell?.value,
                                          value: valCell?.value
                                    };
                              });
                              setTableData(processedTable);
                              // Show unsaved status
                              setTableHeaderTime(`${data.executionTime} (Unsaved)`);

                              setIsPreviewing(true);
                              setSelectedSnapshotTime(null); // Deselect saved snapshots
                              message.success("Dữ liệu đã được làm mới. Vui lòng bấm Lưu để ghi lại.");
                        }
                  } else {
                        message.error(res?.data?.message || "Không thể làm mới dữ liệu");
                  }

                  if (res?.data?.code === 200) {
                        // MANUALLY UPDATE CHART WITH PREVIEW DATA
                        // We don't call API because data is unsaved. We append the new value to the existing chart.
                        const data = res.data.data;
                        if (data?.statistics && chartData && chartMetricId && metricMap[chartMetricId]) {
                              const metricName = metricMap[chartMetricId].name;
                              // Find value in statistics (data.statistics is raw API array: [{name:.., value:..},..])
                              // But we also have processedTable which is easier? Accessing raw is safer.
                              const rawRow = (data.statistics || []).find(row => {
                                    const nameCell = row.find(r => r.name === "Tên số liệu") || row[0];
                                    return nameCell?.value === metricName;
                              });

                              if (rawRow) {
                                    const valCell = rawRow.find(r => r.name === "Giá trị") || rawRow[1];
                                    const rawValue = valCell?.value;

                                    // Parse value (remove commas if formatted?)
                                    // API value might be string "1,234". 
                                    const numValue = typeof rawValue === 'string' ? parseFloat(rawValue.replace(/,/g, '')) : rawValue;

                                    // Clone Chart Data
                                    const newChartData = JSON.parse(JSON.stringify(chartData)); // Deep clone safe

                                    // Find the series. Usually there's only one series for 1 metric.
                                    // Structure: data: [ { name: "...", data: [{date:.., value:..}] } ]
                                    if (newChartData.data && newChartData.data.length > 0) {
                                          const series = newChartData.data[0]; // Assume first series is the one
                                          const today = dayjs();
                                          const [start, end] = chartDateRange;

                                          // Only update if today is within the selected range
                                          if (chartDateRange && chartDateRange.length === 2 &&
                                                (today.isSame(start, 'day') || today.isAfter(start, 'day')) &&
                                                (today.isSame(end, 'day') || today.isBefore(end, 'day'))) {

                                                const newPoint = {
                                                      date: today.format('DD/MM/YYYY'), // Use today as execution time
                                                      value: numValue
                                                };
                                                // Check if last point is today? If so, replace/update? Or just push?
                                                // Guide doesn't specify replacing. Just "preview". We append.
                                                series.data.push(newPoint);

                                                setChartData(newChartData);
                                          }
                                    }
                              }
                        }
                  }
            } catch (e) {
                  console.error(e);
                  message.error("Có lỗi xảy ra khi refresh");
            } finally {
                  setIsRefreshing(false);
            }
      };



      const handleSaveSnapshot = async () => {
            setIsSaving(true);
            try {
                  const res = await saveSnapshot({ campaignId: selectedCampaignId });
                  if (res.data?.code === 200 || res.data?.status === 1) {
                        message.success("Đã lưu dữ liệu thành công");
                        setIsPreviewing(false);

                        // Reload filter options to get new snapshot time
                        const optRes = await getFilterOptions();
                        if (optRes?.data?.data?.campaigns) {
                              // Process campaigns: Reverse metrics for each
                              const updatedCamps = (optRes.data.data.campaigns || []).map(c => ({
                                    ...c,
                                    metrics: (c.metrics || []).slice().reverse()
                              }));
                              setCampaigns(updatedCamps);
                              const currentCamp = updatedCamps.find(c => c.id === selectedCampaignId);
                              if (currentCamp) {
                                    setSnapshotTimes(currentCamp.snapshotTimes || []);
                                    // Select the new snapshot (assuming it's the latest/first one now)
                                    // The guide says reload filter options, snapshot appears in list.
                                    // We should probably select it.
                                    // Assuming backend puts newest first
                                    if (currentCamp.snapshotTimes?.length > 0) {
                                          const newSnapshot = currentCamp.snapshotTimes[0];
                                          setSelectedSnapshotTime(newSnapshot);
                                          // We could reload data, or just assume the current table data is correct (it is, since we just saved it).
                                          // However to be safe and update the header time properly (remove "Unsaved"):
                                          setTableHeaderTime(newSnapshot);
                                    }
                              }
                        }
                  } else {
                        message.error(res?.data?.message || "Lỗi khi lưu dữ liệu");
                  }
            } catch (e) {
                  message.error("Có lỗi xảy ra");
            } finally {
                  setIsSaving(false);
            }
      };

      // =========================
      // RENDER HELPERS
      // =========================

      const columns = [
            {
                  title: 'Tên số liệu',
                  dataIndex: 'name',
                  key: 'name',
                  render: (text) => <span>{text}</span>
            },
            {
                  title: 'Giá trị',
                  dataIndex: 'value',
                  key: 'value',
                  align: 'right', // Align right as per typical number columns, though design image shows left/center? Usually numbers are right aligned. Checking image closely... image value is right aligned.
                  render: (text) => <b>{text}</b>
            }
      ];

      return (
            <div className="container-fluid space-y-5">
                  {/* Header */}
                  <div>
                        <h2 className="text-2xl font-bold mb-4">{PAGE_TITLE}</h2>

                        {/* Campaign Tabs */}
                        <div className="flex gap-2 flex-wrap mb-4">
                              {campaigns.map(camp => {
                                    const isActive = camp.id === selectedCampaignId;
                                    return (
                                          <Button
                                                key={camp.id}
                                                type={isActive ? "primary" : "default"}
                                                className={isActive ? "bg-blue-600 text-white border-blue-600" : "bg-gray-100 text-gray-700 border-transparent hover:bg-gray-200"}
                                                onClick={() => handleSelectCampaign(camp)}
                                                style={{ borderRadius: '6px' }}
                                          >
                                                {camp.name}
                                          </Button>
                                    );
                              })}
                              <Button type="dashed" icon={<span>+</span>} style={{ borderRadius: '6px' }} onClick={() => message.info("Chưa tích hợp")}>Thêm</Button>
                        </div>
                  </div>

                  {/* Chart Section */}
                  <Card className="shadow-sm border-0" styles={{ body: { padding: '24px' } }}>
                        <div className="flex gap-4 mb-4">
                              <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Metrics</span>
                                    <Select
                                          value={chartMetricId}
                                          style={{ width: 200 }}
                                          onChange={handleChartMetricChange}
                                          options={currentMetrics.map(m => ({ label: m.name, value: m.id }))}
                                    />
                              </div>
                              <div className="flex items-center gap-2">
                                    <span className="text-gray-500 whitespace-nowrap">Thời gian:</span>
                                    <DatePicker.RangePicker
                                          value={chartDateRange}
                                          onChange={handleDateRangeChange}
                                          format="DD/MM/YYYY"
                                          allowClear={false}
                                    />
                              </div>
                        </div>

                        <div style={{ minHeight: '300px' }}>
                              {chartData ? (
                                    <MultiaxisLineChart Unprocessed_Data={chartData} unit="" />
                              ) : (
                                    <div className="flex items-center justify-center h-64 text-gray-400">Đang tải dữ liệu biểu đồ...</div>
                              )}
                        </div>

                        {chartData?.total && chartData.total.length > 0 && (
                              <div className="mt-4">
                                    {chartData.total.map((t, idx) => (
                                          <div key={idx} className="font-semibold text-lg">
                                                {t.name}: {formatNumber(t.value)}
                                          </div>
                                    ))}
                              </div>
                        )}
                  </Card>

                  {/* Bottom Section: Table & Saved Data */}
                  <Row gutter={24}>
                        {/* Table Column */}
                        <Col xs={24} lg={16}>
                              <Card className="shadow-sm border-0 h-full" styles={{ body: { padding: '0px' } }}>
                                    <div className="flex justify-between items-center p-4 border-b">
                                          <div className="font-bold text-lg">
                                                Thời điểm ghi nhận: {tableHeaderTime}
                                          </div>
                                          <div className="flex flex-col items-end gap-1">
                                                <div className="flex gap-2">
                                                      <Button
                                                            onClick={handleRefreshSnapshot}
                                                            loading={isRefreshing}
                                                            disabled={isSaving}
                                                            icon={!isRefreshing && <i className="las la-sync mr-1"></i>}
                                                      >
                                                            Refresh
                                                      </Button>
                                                      <Button
                                                            type="primary"
                                                            onClick={handleSaveSnapshot}
                                                            loading={isSaving}
                                                            disabled={!isPreviewing || isRefreshing} // Only enable if previewing unsaved data
                                                            icon={!isSaving && <i className="las la-save mr-1"></i>}
                                                      >
                                                            Lưu
                                                      </Button>
                                                </div>
                                                {isRefreshing && (
                                                      <span className="text-blue-500 text-sm italic">
                                                            Đang thực hiện đồng thời các queries...
                                                      </span>
                                                )}
                                          </div>
                                    </div>
                                    <div className="p-0">
                                          <Table
                                                columns={columns}
                                                dataSource={tableData}
                                                pagination={false}
                                                rowClassName={(record, index) => index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} // Striped
                                                showHeader={true} // Image shows separate header, table has headers "Tên số liệu", "Giá trị" ? Actually image shows table headers inside the card.
                                          />
                                    </div>
                              </Card>
                        </Col>

                        {/* Dimensions (Snapshot List) Column */}
                        <Col xs={24} lg={8}>
                              <Card className="shadow-sm border-0 h-full" styles={{ body: { padding: '0px' } }}>
                                    <div className="p-4 border-b">
                                          <div className="font-bold text-lg mb-1">Dữ liệu đã lưu</div>
                                          <div className="text-sm">
                                                <span className="text-blue-400">Tự động lưu dữ liệu mỗi ngày: </span>
                                                <span className="text-blue-700 font-bold">ON</span>
                                          </div>
                                    </div>
                                    <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
                                          {snapshotTimes.map((time, index) => {
                                                const isActive = time === selectedSnapshotTime;
                                                return (
                                                      <div
                                                            key={index}
                                                            onClick={() => handleSnapshotClick(time)}
                                                            className={`p-3 border-b cursor-pointer transition-colors ${isActive ? 'bg-blue-600 text-white' : 'hover:bg-gray-50 text-gray-700'}`}
                                                      >
                                                            {time}
                                                      </div>
                                                );
                                          })}
                                          {snapshotTimes.length === 0 && (
                                                <div className="p-4 text-center text-gray-400">Chưa có dữ liệu snapshot</div>
                                          )}
                                    </div>
                              </Card>
                        </Col>
                  </Row>

                  <style>{`
                /* Custom overrides if needed */
                .ant-table-thead > tr > th {
                    background: #f8f9fa !important;
                    font-weight: 600;
                }
            `}</style>
            </div>
      );
}

export default CampaignReport;
